import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { L10nTranslationService } from 'angular-l10n';
import { ActivatedRoute } from '@angular/router';

import { AdminService } from '@admin/services/admin.service';
import { CbreAlertService } from '@cbre/alerts';
import { environment } from '@env/environment';
import { IOption, PlainOption } from '@shared/models/icommons.model';
import { ILocationPermission, IUser, IUserRequest } from '@shared/models/iuser.model';
import { AppStorageService, REQUEST_SENT, USER_INFO_TOKEN } from '@shared/services/app-storage.service';
import { CountryId } from '@shared/models/country.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

export const PENDING_STATUS = 'pending';
const REQUEST_ACCESS_FORM_GROUP = {
  role: ['', Validators.required],
  userType: ['', Validators.required],
  country: ['', Validators.required],
};

// * CEP Roles not allowed on US instance...
enum ForbiddenRoles {
  APACUser = 4,
  EMEAUser = 5,
  InvestorBroker = 6,
  OccupierBroker = 7,
}

interface RouteParamsInterface {
  roleOptions: IOption[];
  userTypeOptions: IOption[];
  userInfo: IUser;
}

@Component({
  selector: 'miq-access-request',
  templateUrl: './access-request.page.html',
  styleUrls: ['./access-request.page.scss'],
  standalone: false,
})
export class AccessRequestPageComponent implements OnInit, OnDestroy {
  appName = 'Market IQ';
  userName: string;
  accessFormGroup: UntypedFormGroup;
  roleOptions: IOption[];
  userTypeOptions: IOption[];
  countryOptions: PlainOption[];
  userInfo: IUser;
  isRequestSent: boolean;
  locationPermissions = [];
  isSubmitted: boolean = false;
  usKey = CountryId['United States of America'];
  cepHost = environment.cepHost;

  private pendingStatusId: number;
  private pendingStatusVal: string;

  get disabledSubmitButton(): boolean {
    return this.isSubmitted || !this.accessFormGroup.valid;
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private admin: AdminService,
    private activatedRoute: ActivatedRoute,
    private storage: AppStorageService,
    private cbreAlertService: CbreAlertService,
    public translationService: L10nTranslationService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    this.accessFormGroup = this.formBuilder.group(REQUEST_ACCESS_FORM_GROUP);
    const data = this.activatedRoute.snapshot.data as RouteParamsInterface;
    this.initRouteData(data);
  }

  ngOnInit() {
    /* required for static lable translation */
    this.admin.getCountryList().subscribe(res => (this.countryOptions = res.filter(r => r.key === this.usKey)));
    this.admin.getUserStatuses().subscribe(this.initUserStatuses.bind(this));
    const requestSent = this.storage.getItem(REQUEST_SENT);
    this.isRequestSent = requestSent === this.userInfo.emailAddress;
  }

  get role() {
    return this.accessFormGroup.get('role');
  }
  get userType() {
    return this.accessFormGroup.get('userType');
  }

  get country() {
    return this.accessFormGroup.get('country');
  }

  get details() {
    return this.accessFormGroup.get('details');
  }

  addFormControl(formControl: UntypedFormControl): void {
    const { details } = this.accessFormGroup.controls;
    if (details) {
      this.accessFormGroup.patchValue({ details: formControl.value });
    } else {
      this.accessFormGroup.addControl('details', formControl);
    }
  }

  resetData() {
    this.role.setValue('');
    this.userType.setValue('');
    this.accessFormGroup.controls['details'].setValue([]);
  }

  requestAgain() {
    this.isRequestSent = false;
    this.isSubmitted = false;
    this.storage.removeItem(REQUEST_SENT);
    this.locationPermissions = this.accessFormGroup.value.details;
  }

  submitRequest() {
    const { firstName, lastName, emailAddress } = this.userInfo;
    const user: IUser = {
      firstName,
      lastName,
      emailAddress,
      statusId: this.pendingStatusId,
      statusVal: this.pendingStatusVal,
      userTypeId: this.userType.value,
      userTypeVal: this.userTypeOptions.find(({ key }) => key === this.userType.value).value,
      roleId: this.role.value,
      roleVal: this.roleOptions.find(role => role.key === this.role.value).value,
      createBy: emailAddress,
      updateBy: emailAddress,
      userCountrySkey: this.country.value,
    };
    const details = <ILocationPermission[]>this.details.value;
    this.createUser(user, details);
  }

  private initRouteData({ roleOptions, userTypeOptions, userInfo }: RouteParamsInterface) {
    const forbiddenRoles = Object.values(ForbiddenRoles);
    this.roleOptions = roleOptions.filter(role => !forbiddenRoles.some(fRole => fRole === role.key));
    this.userTypeOptions = userTypeOptions;
    this.isRequestSent = !!userInfo && userInfo.statusVal?.toLowerCase() === PENDING_STATUS;
    this.userName = !!userInfo ? `${userInfo.firstName} ${userInfo.lastName}` : '';
    this.userInfo = userInfo;
  }

  private initUserStatuses(userStatuses: IOption[]) {
    const statusId = userStatuses.find(status => status.value.toLowerCase() === PENDING_STATUS);
    this.pendingStatusId = statusId.key;
    this.pendingStatusVal = statusId.value;
  }

  private createUser(user: IUser, details: ILocationPermission[]): void {
    this.admin
      .postNewUser(new IUserRequest(user, details))
      .subscribe({ next: this.onCreateUserSuccess.bind(this, user), error: this.onCreateUserFailure.bind(this) });
  }

  private onCreateUserSuccess(user, userId) {
    const userToSave = this.storage.getItem<IUser>(USER_INFO_TOKEN);
    user.id = userId;
    this.isRequestSent = true;
    this.isSubmitted = true;
    this.storage.setItem(REQUEST_SENT, userToSave.emailAddress);
    this.storage.setItem<IUser>(USER_INFO_TOKEN, { ...userToSave, ...user });
  }

  private onCreateUserFailure(error) {
    this.logger.error(error);
    this.isSubmitted = false;
    this.cbreAlertService.showInfoSnackBar_l10n('core.userCantCreate');
  }

  ngOnDestroy() {
    /** label translation purpose added */
  }
}
